import { databases } from '@/lib/appwrite';
import { sendVisitToN8n } from '@/visits/visit/views/weebhook/addEventCalendarn8n';
import { ID } from 'appwrite';

const DB_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const VISITS_COLLECTION_ID = import.meta.env.VITE_VISITS_COLLECTION_ID;

interface CreateVisitData {
  userId: string;
  organizationId: string;
  visitPurpose: string;
  startDate: Date;
  startTime: string; // formato "HH:mm:ss"
  endTime: string;   // formato "HH:mm:ss"
  branch?: string;
  userEmail: string;
  collaboratorIds?: string[];
  collaboratorsEmails?: string[];
  sameMotif?: boolean;
}

const formatDateTime = (date: Date, time: string): string => {
  const [hours, minutes, seconds] = time.split(':');

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const formattedTime = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${(seconds || '00').padStart(2, '0')}`;

  const localDateStr = `${year}-${month}-${day}T${formattedTime}`;
  const localDate = new Date(localDateStr);

  return localDate.toISOString();
};

const buildVisitPayload = (
  userId: string,
  data: CreateVisitData,
  creationDate: string,
  startDateTime: string,
  endDateTime: string,
  usersId?: string[]
) => ({
  userId,
  organizationId: data.organizationId,
  status: 'pending',
  visitDates: [creationDate, startDateTime, endDateTime],
  visitPurpose: data.visitPurpose.trim() || null,
  userComment: null,
  branch: data.branch?.trim() || null,
  usersId: usersId ?? null,

});

export const createVisit = async (data: CreateVisitData) => {
  try {
    const now = new Date();
    const creationDate = now.toISOString();
    const startDateTime = formatDateTime(data.startDate, data.startTime);
    const endDateTime = formatDateTime(data.startDate, data.endTime);

    const ORGS_COLLECTION_ID = import.meta.env.VITE_ORGANIZATIONS_COLLECTION_ID;
    const organization = await databases.getDocument(
      DB_ID,
      ORGS_COLLECTION_ID,
      data.organizationId
    );

    const createdIds: string[] = [];

    try {
      let responses;

      if (data.sameMotif && (data.collaboratorIds?.length ?? 0) > 0) {
        // Un solo registro con usersId = IDs de acompañantes
        const doc = await databases.createDocument(
          DB_ID,
          VISITS_COLLECTION_ID,
          ID.unique(),
          buildVisitPayload(data.userId, data, creationDate, startDateTime, endDateTime, data.collaboratorIds)
        );
        createdIds.push(doc.$id);
        responses = [doc];
      } else {
        // Registro individual por cada usuario
        const allUserIds = [data.userId, ...(data.collaboratorIds ?? [])];
        responses = await Promise.all(
          allUserIds.map(async (uid) => {
            const doc = await databases.createDocument(
              DB_ID,
              VISITS_COLLECTION_ID,
              ID.unique(),
              buildVisitPayload(uid, data, creationDate, startDateTime, endDateTime)
            );
            createdIds.push(doc.$id);
            return doc;
          })
        );
      }

      const webhookResult = await sendVisitToN8n(
        data.visitPurpose,
        startDateTime,
        endDateTime,
        organization.name,
        organization.city,
        data.branch || 'Matriz',
        data.userEmail,
        data.collaboratorsEmails ?? []
      );

      return {
        success: true,
        data: responses,
        webhookSent: webhookResult.success
      };

    } catch (innerError) {
      console.warn('Fallo en la creación. (Rollback)...');
      await Promise.allSettled(
        createdIds.map(id =>
          databases.deleteDocument(DB_ID, VISITS_COLLECTION_ID, id)
        )
      );
      throw innerError;
    }

  } catch (error: any) {
    console.error('Error creating visit:', error);
    return {
      success: false,
      error: error.message || 'Error al guardar la visita'
    };
  }
};

export const validateVisitData = (data: CreateVisitData): string | null => {
  if (!data.userId) return 'Usuario no identificado';
  if (!data.organizationId) return 'Debe seleccionar una organización';
  if (!data.visitPurpose?.trim()) return 'El objetivo de visita es requerido';
  if (!data.startDate) return 'Debe seleccionar una fecha';
  if (!data.startTime) return 'Debe seleccionar hora inicial';
  if (!data.endTime) return 'Debe seleccionar hora de salida';

  const [startH, startM] = data.startTime.split(':').map(Number);
  const [endH, endM] = data.endTime.split(':').map(Number);

  if ((endH * 60 + endM) <= (startH * 60 + startM)) {
    return 'La hora de salida debe ser posterior a la hora inicial';
  }

  return null;
};